import {
  Field,
  FilteractionObject,
  formatDateTime,
  SituationObject
} from '@yes.technology/react-toolkit'

import { AllowedLocales } from 'shared/utils/date/formatDate'
import { isSituationObjectclass } from 'shared/utils/specialRelationFields/specialRelationFields'
import { normalizeOperator } from '../../shared/fields'
import { decodeQuery } from '../../shared/FilterLanguage'
import { FieldValues, FieldValuesValue } from '../../types'

type GetValueProps = {
  currentField?: Field
  value: FieldValuesValue
  locale: AllowedLocales
  situations: Pick<
    SituationObject,
    'code' | 'des' | 'uuid' | 'uuid_Objectclass'
  >[]
}

const getValue = ({
  currentField,
  locale,
  value,
  situations
}: GetValueProps): FieldValuesValue => {
  if (value && currentField?.type === 'DATETIME') {
    return formatDateTime(new Date(`${value}`), {
      format: 'date-time',
      locale,
      timezone: window.waTimezone
    })
  } else if (
    !currentField?.relation_objectclass_uuid ||
    !isSituationObjectclass(currentField.relation_objectclass_uuid)
  ) {
    return value
  }

  const situation = situations.find(({ code }) => code === value)

  return situation
    ? [
        {
          code: situation.code,
          des: situation.des,
          uuid: situation.uuid,
          uuid_Objectclass: situation.uuid_Objectclass
        }
      ]
    : value
}

type NormalizeFilterGroupProps = {
  filterGroup: {
    [fieldKey: string]: Pick<FieldValues[''], 'value' | 'operator'>
  }
  sections: Field[][]
  situations: GetValueProps['situations']
  locale: AllowedLocales
}

const normalizeFilterGroup = ({
  filterGroup,
  sections,
  situations,
  locale
}: NormalizeFilterGroupProps): FieldValues =>
  Object.fromEntries(
    Object.entries(filterGroup).map(([fieldKey, fieldValue]) => {
      const currentField = sections
        .flat()
        .find((field) => field?.column_json === fieldKey)
      const { type = 'STRING' } = currentField || {}
      const { value, operator: originalOperator } = fieldValue

      return [
        fieldKey,
        {
          value: getValue({
            situations,
            value,
            locale,
            currentField
          }),
          operator: normalizeOperator({
            originalOperator,
            fieldType: type
          }),
          type
        }
      ]
    })
  )

type PopulateFilterStateProps = {
  initialFieldValues: FieldValues
  values: {
    [group: string]: {
      [fieldKey: string]: Pick<FieldValues[''], 'value' | 'operator'>
    }
  }
  sections: Field[][]
  situations: GetValueProps['situations']
  locale: AllowedLocales
}

const getQueryData = ({
  initialFieldValues,
  values,
  sections,
  situations,
  locale
}: PopulateFilterStateProps) => {
  const changedKeysData: Record<string, FieldValues> = {}
  const queryData: Record<string, FieldValues> = {}

  for (const [groupKey, filterGroup] of Object.entries(values)) {
    const normalized = normalizeFilterGroup({
      filterGroup,
      sections,
      situations,
      locale
    })

    changedKeysData[groupKey] = normalized
    queryData[groupKey] = { ...initialFieldValues, ...normalized }
  }

  return [queryData, changedKeysData]
}

type Filteraction = Pick<
  FilteractionObject,
  'uuid' | 'des' | 'query' | 'complement'
>

type getQueryDataFromFilteractionProps = {
  sections: PopulateFilterStateProps['sections']
  situations: GetValueProps['situations']
  initialFieldValues: FieldValues
  filteraction: Filteraction
  locale: AllowedLocales
}

const getQueryDataFromFilteraction = ({
  sections: fields,
  situations,
  initialFieldValues,
  filteraction,
  locale
}: getQueryDataFromFilteractionProps) => {
  const { values, composition } = decodeQuery(filteraction.query)

  const [queryData, changedKeysData] = getQueryData({
    initialFieldValues,
    values,
    sections: fields,
    situations,
    locale
  })

  return { queryData, changedKeysData, composition }
}

export default getQueryDataFromFilteraction
